<template lang="pug">
  .pho-file-drag-and-drop.pw-100.ph-100
    div.pw-100.ph-100.pa-2(
      :class="areaClass"
      @drop="onDrop"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
    )
      slot
      div.d-block.pw-100.ph-100
        form(ref="fileInputFormRef")
          input.drag-and-drop-input(
            id="phoFileDragAndDrop"
            type="file"
            ref="phoDragAndDropInputRef"
            :multiple="multiple"
            :accept="accept"
            @change="onInputChanged"
          )
          slot(name="activator" :labelClass="labelClass" :on="on")
            div.darken-5.d-flex.align-center.justify-center.cursor-pointer.pa-3.pw-100.ph-100(
              v-on="on"
              :class="labelClass"
            )
              | {{ $t($lang.file.DRAG_AND_DROP_THE_FILES_YOU_WANT_TO_UPLOAD) }}
    MediaPicker(
      ref="mediaPickerRef"
      :multiple="multiple"
      :showAddPhotoInformation="showAddPhotoInformation"
      @onSelectFile="onSelectFile"
      @onFilesSelected="onFilesSelected"
    )
</template>

<script>
import { ref } from 'vue';

import { MediaPicker } from '../../media/media-picker';

export default {
  name: 'PhoFileDragAndDrop',
  components: {
    MediaPicker,
  },
  props: {
    accept: {
      type: String,
      default: '*',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    disableMediaPicker: {
      type: Boolean,
      default: false,
    },
    showAddPhotoInformation: {
      type: Boolean,
      default: false,
    },
    beforeAction: {
      type: Function,
      default: null,
    },
  },
  emits: ['onChange', 'onDrop', 'onDragLeave', 'onDragOver'],
  setup(props, { emit }) {
    const areaClasses = {
      default: 'drop-area',
      onDropping: 'drop-area dropping',
    };

    const labelClasses = {
      default: 'blue',
      onDropping: 'green',
    };

    const phoDragAndDropInputRef = ref(null);
    const fileInputFormRef = ref(null);
    const mediaPickerRef = ref(null);
    const areaClass = ref(areaClasses.default);
    const labelClass = ref(labelClasses.default);

    const clear = () => {
      fileInputFormRef.value.reset();
    };

    const onChange = async filesObject => {
      emit('onChange', {
        target: {
          result: {
            files: filesObject.target.result.files,
          },
        },
      });
    };

    const onInputChanged = () => {
      if (phoDragAndDropInputRef.value?.files?.length) {
        onChange({
          target: {
            result: {
              files: Object.values(phoDragAndDropInputRef.value.files).map(f => ({ file: f })),
            },
          },
        });
      }
    };

    const onDrop = event => {
      event.preventDefault();

      if (typeof props.beforeAction === 'function') {
        props.beforeAction();
      }
      phoDragAndDropInputRef.value.files = event.dataTransfer.files;
      onInputChanged();
      areaClass.value = areaClasses.default;
      labelClass.value = labelClasses.default;
      emit('onDrop', event);
    };

    const onDragOver = event => {
      event.preventDefault();

      if (labelClass.value === labelClasses.default) {
        areaClass.value = areaClasses.onDropping;
        labelClass.value = labelClasses.onDropping;
      }

      emit('onDragOver', event);
    };

    const onDragLeave = event => {
      areaClass.value = areaClasses.default;
      labelClass.value = labelClasses.default;
      emit('onDragLeave', event);
    };

    const onSelectFile = () => {
      phoDragAndDropInputRef.value.click();
    };

    const pickMedia = () => {
      if (typeof props.beforeAction === 'function') {
        props.beforeAction();
      }

      if (props.disableMediaPicker) {
        onSelectFile();

        return;
      }

      mediaPickerRef.value.pick();
    };

    const onFilesSelected = result => {
      if (!(result.files || []).length) {
        return;
      }

      onChange({ target: { result } });
    };

    return {
      on: {
        click: pickMedia,
      },
      onSelectFile,
      onFilesSelected,
      phoDragAndDropInputRef,
      fileInputFormRef,
      mediaPickerRef,
      areaClass,
      labelClass,
      clear,
      onChange,
      onInputChanged,
      onDrop,
      onDragOver,
      onDragLeave,
    };
  },
};
</script>

<style lang="scss">
.pho-file-drag-and-drop {
  .drop-area {
    border: 2px dashed #dadada;
  }

  .drop-area.dropping {
    border-color: #bababa;
  }

  .drag-and-drop-input {
    display: none;
  }
}
</style>
